import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { RequireFullAuth, RequireTempAuth } from './auth/RequireAuth.js';
import { RequirePlatformAdmin } from './auth/RequirePlatformAdmin.js';
import { AppShell } from './components/AppShell.js';
import { SyncStatusBanner } from './components/SyncStatusBanner.js';
import { AdminBillingPage } from './pages/AdminBilling.js';
import { AdminDashboardPage } from './pages/AdminDashboard.js';
import { AdminInsightsPage } from './pages/AdminInsights.js';
import { AdminPaymentDetailPage } from './pages/AdminPaymentDetail.js';
import { AdminSubscriptionsPage } from './pages/AdminSubscriptions.js';
import { AdminSurveyEditorPage } from './pages/AdminSurveyEditor.js';
import { AdminSurveysListPage } from './pages/AdminSurveys.js';
import { AdminUsersPage } from './pages/AdminUsers.js';
import { AssessmentCapturePage } from './pages/AssessmentCapture.js';
import { AssessmentClosedPage } from './pages/AssessmentClosed.js';
import { AssessmentNewPage } from './pages/AssessmentNew.js';
import { AuditMeetingsPage } from './pages/AuditMeetings.js';
import { IsoMatrixPage } from './pages/IsoMatrix.js';
import { ClientDetailPage, SiteAssessmentsPage } from './pages/ClientDetail.js';
import { ClientNewPage } from './pages/ClientNew.js';
import { ClientsListPage } from './pages/ClientsList.js';
import { FindingDetailPage } from './pages/FindingDetail.js';
import { FindingsListPage } from './pages/FindingsList.js';
import { LoginPage } from './pages/Login.js';
import { MfaSetupPage } from './pages/MfaSetup.js';
import { MfaVerifyPage } from './pages/MfaVerify.js';
import { CapturePage } from './pages/Capture.js';
import { CaptureResultPage } from './pages/CaptureResult.js';
import { DemoPage } from './pages/Demo.js';
import { PortalPage } from './pages/Portal.js';
import { SignupPage } from './pages/Signup.js';
import { SyncStatusPage } from './pages/SyncStatus.js';
import { PlatformBillingPage } from './pages/PlatformBilling.js';
import { PlatformConsole } from './pages/PlatformConsole.js';
import { PlatformIntegrationsPage } from './pages/PlatformIntegrations.js';
import { PlatformTenantsPage } from './pages/PlatformTenants.js';
import { PlatformUsersPage } from './pages/PlatformUsers.js';

/**
 * LandingPage se carga con React.lazy para que el bundle de la landing
 * (~40 KB de copy/SVG comercial) NO entre al chunk principal de la app.
 * Los auditores en campo con 4G solo descargan la app autenticada (/clients, etc.).
 * Tech-design §1.4 — primera lazy en el repo, introducida aquí.
 */
const LandingPage = lazy(() =>
  import('./pages/Landing.js').then((m) => ({ default: m.LandingPage })),
);

export function App(): JSX.Element {
  const location = useLocation();
  // Determina el variant del shell según el pathname (tech-design §5.7).
  // ADR-038: activar variant platform en /platform y todas las rutas hijas
  const shellVariant =
    location.pathname === '/platform' || location.pathname.startsWith('/platform/')
      ? 'platform'
      : 'tenant';

  return (
    <AppShell variant={shellVariant}>
      <SyncStatusBanner />
      <Routes>
        <Route path="/portal/:token" element={<PortalPage />} />
        <Route path="/capture/:token" element={<CapturePage />} />
        {/* ADR-041 D2: pagina publica de resultados post-autoevaluacion del cliente */}
        <Route path="/capture/:token/resultado" element={<CaptureResultPage />} />
        {/* Landing comercial pública — lazy-loaded para no inflar bundle de auditores */}
        <Route
          path="/"
          element={
            <Suspense fallback={null}>
              <LandingPage />
            </Suspense>
          }
        />
        {/* Alias retro-compatible con deep-links existentes (Demo, Signup, emails) */}
        <Route
          path="/pricing"
          element={
            <Suspense fallback={null}>
              <LandingPage />
            </Suspense>
          }
        />
        <Route path="/demo" element={<DemoPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/mfa/setup"
          element={
            <RequireTempAuth>
              <MfaSetupPage />
            </RequireTempAuth>
          }
        />
        <Route
          path="/mfa/verify"
          element={
            <RequireTempAuth>
              <MfaVerifyPage />
            </RequireTempAuth>
          }
        />

        <Route
          path="/clients"
          element={
            <RequireFullAuth>
              <ClientsListPage />
            </RequireFullAuth>
          }
        />
        <Route
          path="/clients/new"
          element={
            <RequireFullAuth>
              <ClientNewPage />
            </RequireFullAuth>
          }
        />
        <Route
          path="/clients/:id"
          element={
            <RequireFullAuth>
              <ClientDetailPage />
            </RequireFullAuth>
          }
        />
        <Route
          path="/sites/:siteId/assessments"
          element={
            <RequireFullAuth>
              <SiteAssessmentsPage />
            </RequireFullAuth>
          }
        />
        <Route
          path="/sites/:siteId/assessments/new"
          element={
            <RequireFullAuth>
              <AssessmentNewPage />
            </RequireFullAuth>
          }
        />
        <Route
          path="/assessments/:id/capture"
          element={
            <RequireFullAuth>
              <AssessmentCapturePage />
            </RequireFullAuth>
          }
        />
        <Route
          path="/assessments/:id/closed"
          element={
            <RequireFullAuth>
              <AssessmentClosedPage />
            </RequireFullAuth>
          }
        />
        <Route
          path="/assessments/:id/meetings"
          element={
            <RequireFullAuth>
              <AuditMeetingsPage />
            </RequireFullAuth>
          }
        />
        <Route
          path="/assessments/:id/iso-matrix"
          element={
            <RequireFullAuth>
              <IsoMatrixPage />
            </RequireFullAuth>
          }
        />
        <Route
          path="/findings"
          element={
            <RequireFullAuth>
              <FindingsListPage />
            </RequireFullAuth>
          }
        />
        <Route
          path="/findings/:id"
          element={
            <RequireFullAuth>
              <FindingDetailPage />
            </RequireFullAuth>
          }
        />
        <Route
          path="/sync"
          element={
            <RequireFullAuth>
              <SyncStatusPage />
            </RequireFullAuth>
          }
        />
        <Route
          path="/admin/surveys"
          element={
            <RequireFullAuth>
              <AdminSurveysListPage />
            </RequireFullAuth>
          }
        />
        <Route
          path="/admin/surveys/new"
          element={
            <RequireFullAuth>
              <AdminSurveyEditorPage />
            </RequireFullAuth>
          }
        />
        <Route
          path="/admin/surveys/:id/edit"
          element={
            <RequireFullAuth>
              <AdminSurveyEditorPage />
            </RequireFullAuth>
          }
        />
        <Route
          path="/admin/users"
          element={
            <RequireFullAuth>
              <AdminUsersPage />
            </RequireFullAuth>
          }
        />
        <Route
          path="/admin/dashboard"
          element={
            <RequireFullAuth>
              <AdminDashboardPage />
            </RequireFullAuth>
          }
        />
        <Route
          path="/admin/insights"
          element={
            <RequireFullAuth>
              <AdminInsightsPage />
            </RequireFullAuth>
          }
        />
        <Route
          path="/admin/billing"
          element={
            <RequireFullAuth>
              <AdminBillingPage />
            </RequireFullAuth>
          }
        />
        <Route
          path="/admin/billing/payments/:id"
          element={
            <RequireFullAuth>
              <AdminPaymentDetailPage />
            </RequireFullAuth>
          }
        />
        <Route
          path="/admin/billing/subscriptions"
          element={
            <RequireFullAuth>
              <AdminSubscriptionsPage />
            </RequireFullAuth>
          }
        />

        {/*
         * Rutas /platform/* — consola unificada super_admin (ADR-038).
         * RequireFullAuth + RequirePlatformAdmin aplicados UNA sola vez en el padre.
         * Las páginas hijo se montan a través del <Outlet/> de PlatformConsole.
         */}
        <Route
          path="/platform"
          element={
            <RequireFullAuth>
              <RequirePlatformAdmin>
                <PlatformConsole />
              </RequirePlatformAdmin>
            </RequireFullAuth>
          }
        >
          {/* Índice: /platform → /platform/tenants */}
          <Route index element={<Navigate to="/platform/tenants" replace />} />
          {/* BB-PLATFORM-01 */}
          <Route path="tenants" element={<PlatformTenantsPage />} />
          {/* BB-PLATFORM-02 */}
          <Route path="users" element={<PlatformUsersPage />} />
          {/* DD-PLATFORM-01 */}
          <Route path="billing" element={<PlatformBillingPage />} />
          {/* FF-PLATFORM-01: tab APIs / integraciones (ADR-039) */}
          <Route path="apis" element={<PlatformIntegrationsPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/clients" replace />} />
      </Routes>
    </AppShell>
  );
}
